import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, VideoOff, WifiOff, X } from "lucide-react";
import { useDeviceFrame } from "@/hooks/useDeviceFrame";

const DETAIL_POLL_MS = 80;
const CONTROLS_HIDE_MS = 2400;
const SWIPE_THRESHOLD_PX = 55;

function fullscreenState({ enabled, runtime, signal, src }) {
  if (!enabled) return { label: "Désactivée", dot: "bg-zinc-500", tone: "text-zinc-300", icon: VideoOff };
  if (runtime !== "online") return { label: "Hors ligne", dot: "bg-red-500", tone: "text-zinc-300", icon: WifiOff };
  if (signal === "waiting" || !src) return { label: "Connexion…", dot: "bg-amber-400", tone: "text-zinc-200", icon: null };
  if (signal === "lost") return { label: "Hors ligne", dot: "bg-red-500", tone: "text-zinc-300", icon: WifiOff };
  return { label: "En ligne", dot: "bg-emerald-500", tone: "text-zinc-100", icon: null };
}

export function DeviceFullscreenOverlay({ camera, cameras = [], onCameraChange, onClose }) {
  const runtime = camera?.runtime_status || "offline";
  const active = Boolean(camera?.enabled && runtime === "online");
  const { src, signal } = useDeviceFrame({ cameraId: camera?.id, active, pollMs: DETAIL_POLL_MS });

  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef(null);
  const touchStartRef = useRef(null);
  const currentIndex = cameras.findIndex((item) => item.id === camera?.id);
  const canNavigate = cameras.length > 1 && currentIndex >= 0 && typeof onCameraChange === "function";

  const goRelative = useCallback((delta) => {
    if (!canNavigate) return;
    const nextIndex = (currentIndex + delta + cameras.length) % cameras.length;
    onCameraChange(cameras[nextIndex]);
  }, [canNavigate, currentIndex, cameras, onCameraChange]);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
  }, []);

  useEffect(() => {
    showControls();
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [camera?.id, showControls]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") { onClose(); return; }
      if (event.key === "ArrowLeft") { event.preventDefault(); goRelative(-1); showControls(); }
      if (event.key === "ArrowRight") { event.preventDefault(); goRelative(1); showControls(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goRelative, onClose, showControls]);

  if (!camera) return null;

  const state = fullscreenState({ enabled: camera.enabled, runtime, signal, src });
  const StateIcon = state.icon;
  const live = state.label === "En ligne";

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    showControls();
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    const touch = event.changedTouches?.[0];
    touchStartRef.current = null;
    if (!start || !touch || !canNavigate) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;
    goRelative(dx > 0 ? -1 : 1);
  };

  const controlsClass = controlsVisible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none";

  return createPortal(
    <div
      className="fixed inset-0 z-50 h-[100dvh] w-screen overflow-hidden bg-black text-white"
      style={{ touchAction: "pan-y" }}
      data-testid="camera-fullscreen-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Caméra ${camera.name}`}
      onMouseMove={showControls}
      onMouseDown={showControls}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="absolute inset-0 flex items-center justify-center bg-black">
        {live && src ? (
          <img src={src} alt={camera.name} className="h-full w-full object-contain" data-testid="camera-fullscreen-image" draggable="false" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.04),_transparent_55%)]">
            {StateIcon ? <StateIcon className="size-12 text-zinc-700" strokeWidth={1.2} /> : null}
            <div className={`text-base font-medium ${state.tone} ${state.label === "Connexion…" ? "animate-pulse" : ""}`}>{state.label}</div>
          </div>
        )}
      </div>

      <div
        className={`absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-4 bg-gradient-to-b from-black/75 via-black/25 to-transparent pb-14 transition-opacity duration-200 ${controlsClass}`}
        style={{
          paddingTop: "max(1rem, env(safe-area-inset-top))",
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <div className="min-w-0 rounded-2xl bg-black/25 px-3 py-2 backdrop-blur-sm">
          <div className="truncate text-base font-semibold sm:text-lg" data-testid="camera-fullscreen-name">{camera.name}</div>
          <div className="mt-1 flex items-center gap-2 text-xs sm:text-sm" data-testid="camera-fullscreen-state">
            <span className={`size-1.5 rounded-full ${state.dot}`} />
            <span className={state.tone}>{state.label}</span>
          </div>
        </div>
        <button type="button" onClick={onClose} data-testid="camera-fullscreen-close" className="grid size-11 shrink-0 place-items-center rounded-2xl bg-black/35 text-white/80 backdrop-blur-sm transition hover:bg-black/60 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70" aria-label="Fermer le plein écran" title="Fermer">
          <X className="size-5" strokeWidth={1.8} />
        </button>
      </div>

      {canNavigate && (
        <>
          <button type="button" onClick={() => goRelative(-1)} className={`absolute left-3 top-1/2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-2xl bg-black/35 text-white/75 backdrop-blur-sm transition hover:bg-black/60 hover:text-white sm:left-5 sm:size-12 ${controlsClass}`} data-testid="camera-fullscreen-previous" aria-label="Caméra précédente" title="Caméra précédente">
            <ChevronLeft className="size-7" strokeWidth={1.5} />
          </button>
          <button type="button" onClick={() => goRelative(1)} className={`absolute right-3 top-1/2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-2xl bg-black/35 text-white/75 backdrop-blur-sm transition hover:bg-black/60 hover:text-white sm:right-5 sm:size-12 ${controlsClass}`} data-testid="camera-fullscreen-next" aria-label="Caméra suivante" title="Caméra suivante">
            <ChevronRight className="size-7" strokeWidth={1.5} />
          </button>
        </>
      )}

      {canNavigate && (
        <div
          className={`absolute left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/35 px-3 py-1.5 text-xs text-white/60 backdrop-blur-sm transition-opacity duration-200 ${controlsClass}`}
          style={{ bottom: "max(1rem, env(safe-area-inset-bottom))" }}
          data-testid="camera-fullscreen-position"
        >
          {currentIndex + 1} / {cameras.length}
        </div>
      )}
    </div>,
    document.body,
  );
}
